import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

// Galaxy League hero artwork — an elliptical orbit system built around the
// real logo mark. Three tilted ellipses carry glowing "planets" (SVG
// animateMotion), wrapped by a dotted star-ring, with a subtle parallax tilt
// that reacts to the pointer.
//
// The PNG ships on a black background; `mix-blend-mode: screen` removes the
// black so only the glowing ball blends into the cosmic page.

const ellipsePath = (rx, ry) =>
  `M ${100 - rx},100 a ${rx},${ry} 0 1,0 ${2 * rx},0 a ${rx},${ry} 0 1,0 ${-2 * rx},0`;

// rx, ry, tilt(deg), duration(s), reverse, planet color, planet radius, halo
const ORBITS = [
  { rx: 95, ry: 26, tilt: 8, dur: 11, rev: false, color: "#FFFFFF", r: 2, halo: 6 },
  { rx: 90, ry: 42, tilt: -22, dur: 16, rev: false, color: "#BFD3FF", r: 3, halo: 9 },
  { rx: 76, ry: 58, tilt: 32, dur: 22, rev: true, color: "#5B8CFF", r: 2.6, halo: 8 },
];

// Dotted star-ring (a "hint" of a constellation halo around the system).
const STAR_DOTS = Array.from({ length: 48 }, (_, i) => {
  const a = (i / 48) * Math.PI * 2;
  const r = 47.5;
  return {
    x: 50 + Math.cos(a) * r,
    y: 50 + Math.sin(a) * r,
    size: i % 8 === 0 ? 2.6 : i % 3 === 0 ? 1.6 : 1,
    op: i % 5 === 0 ? 0.85 : 0.35,
    twinkle: i % 6 === 0,
    delay: (i % 7) * 0.45,
  };
});

export const PlanetHeroArt = () => {
  // Parallax tilt
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      data-testid="planet-hero-art"
      onPointerMove={handleMove}
      onPointerLeave={reset}
      className="relative mx-auto aspect-square w-[230px] sm:w-[400px] lg:w-[480px]"
      style={{ perspective: "1000px" }}
    >
      {/* Ambient back glow */}
      <div className="absolute inset-[-8%] rounded-full bg-[radial-gradient(circle_at_50%_48%,rgba(58,123,255,0.42),transparent_62%)] blur-3xl animate-gl-pulse" />
      <div className="absolute inset-[8%] rounded-full bg-[radial-gradient(circle_at_50%_50%,rgba(58,123,255,0.16),transparent_60%)] blur-2xl" />

      <motion.div
        className="absolute inset-0"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
      >
        {/* Dotted star-ring (slow rotation) */}
        <div className="absolute inset-0 animate-gl-spin-slow">
          {STAR_DOTS.map((d, i) => (
            <span
              key={i}
              className={`absolute rounded-full bg-white ${d.twinkle ? "animate-gl-twinkle" : ""}`}
              style={{
                left: `${d.x}%`,
                top: `${d.y}%`,
                width: `${d.size}px`,
                height: `${d.size}px`,
                opacity: d.op,
                transform: "translate(-50%, -50%)",
                boxShadow: "0 0 6px 1px rgba(191,211,255,0.7)",
                animationDelay: `${d.delay}s`,
              }}
            />
          ))}
        </div>

        {/* Orbit system */}
        <svg
          viewBox="0 0 200 200"
          className="absolute inset-0 h-full w-full overflow-visible"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="gl-ring-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#5B8CFF" stopOpacity="0.05" />
              <stop offset="50%" stopColor="#9DBBFF" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#5B8CFF" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          {ORBITS.map((o, i) => {
            const path = ellipsePath(o.rx, o.ry);
            const motionProps = o.rev
              ? { keyPoints: "1;0", keyTimes: "0;1", calcMode: "linear" }
              : {};
            return (
              <g key={i} transform={`rotate(${o.tilt} 100 100)`}>
                <path
                  d={path}
                  fill="none"
                  stroke="url(#gl-ring-grad)"
                  strokeWidth="1.2"
                />
                <g>
                  <circle r={o.halo} fill={o.color} opacity="0.22" />
                  <circle r={o.r} fill={o.color}>
                    <animateMotion
                      dur={`${o.dur}s`}
                      repeatCount="indefinite"
                      rotate="auto"
                      path={path}
                      {...motionProps}
                    />
                  </circle>
                  <animateMotion
                    dur={`${o.dur}s`}
                    repeatCount="indefinite"
                    path={path}
                    {...motionProps}
                  />
                </g>
              </g>
            );
          })}
        </svg>

        {/* The real logo mark — floating + breathing */}
        <motion.div
          className="absolute inset-[20%] animate-gl-float"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          style={{ transform: "translateZ(40px)" }}
        >
          <div className="relative h-full w-full">
            <div className="absolute inset-[10%] rounded-full bg-[radial-gradient(circle_at_50%_45%,rgba(58,123,255,0.6),transparent_65%)] blur-xl animate-gl-pulse" />
            <img
              src="/galaxy-logo.png"
              alt="Galaxy League Logo"
              draggable="false"
              className="relative h-full w-full select-none object-contain"
              style={{
                mixBlendMode: "screen",
                filter:
                  "drop-shadow(0 0 26px rgba(58,123,255,0.55)) drop-shadow(0 0 60px rgba(91,140,255,0.30))",
              }}
            />
          </div>
        </motion.div>
      </motion.div>

      {/* Floating sparkle particles */}
      <motion.span
        className="absolute left-[6%] top-[18%] h-1.5 w-1.5 rounded-full bg-white"
        style={{ boxShadow: "0 0 10px 2px rgba(255,255,255,0.8)" }}
        animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.span
        className="absolute right-[8%] top-[12%] h-2 w-2 rounded-full bg-[#5B8CFF]"
        style={{ boxShadow: "0 0 12px 3px rgba(91,140,255,0.9)" }}
        animate={{ opacity: [0.4, 1, 0.4], scale: [0.7, 1.15, 0.7] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
      />
      <motion.span
        className="absolute bottom-[14%] right-[16%] h-1.5 w-1.5 rounded-full bg-white"
        style={{ boxShadow: "0 0 10px 2px rgba(255,255,255,0.75)" }}
        animate={{ opacity: [0.25, 0.9, 0.25], scale: [0.8, 1.25, 0.8] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
      />
    </div>
  );
};
